import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const AnimatedText = ({ text }: { text: string }) => {
  const words = text.split(' ');
  
  return (
    <p className="text-white/80 text-lg leading-relaxed">
      {words.map((word, i) => (
        <motion.span
          key={i}
          className="inline-block mr-[0.2em]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ 
            duration: 0.5,
            delay: i * 0.05,
            ease: [0.16, 1, 0.3, 1]
          }}
        >
          {word}
        </motion.span>
      ))}
    </p>
  );
};

const ParallaxHero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  
  const y = useTransform(scrollY, [0, 1000], [0, 400]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const scale = useTransform(scrollY, [0, 300], [1, 1.2]);

  const { t } = useTranslation();

  return (
    <div ref={containerRef} className="relative h-screen overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('https://res.cloudinary.com/dansvy9a5/image/upload/v1739113195/1736923111904_2_r6gysq.jpg')",
          y,
          scale
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
      </motion.div>

      <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          style={{ opacity }}
          className="relative max-w-4xl mx-auto"
        >
          <motion.h1 
            className="text-6xl md:text-8xl font-serif mb-6 relative text-white"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-block relative">
              Le Programme
              <motion.div 
                className="absolute -left-4 -right-4 h-px bg-gradient-to-r from-transparent via-[#c9ab81] to-transparent"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </span>
            <br />
            <span className="text-[#c9ab81] italic">Transformateur</span>
          </motion.h1>

          <motion.p
            className="text-xl text-white/90 max-w-2xl mx-auto font-light"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Une expérience immersive unique pour révéler votre plein potentiel
          </motion.p>
        </motion.div>
      </div>

      <motion.div 
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <div className="w-px h-16 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-[#c9ab81] to-transparent animate-scroll-indicator" />
        </div>
      </motion.div>
    </div>
  );
};

const DayCard = ({ 
  day, 
  title, 
  description, 
  image,
  align = 'right'
}: { 
  day: string; 
  title: string; 
  description: string;
  image: string;
  align?: 'left' | 'right';
}) => {
  const isRight = align === 'right';
  
  return (
    <motion.div 
      className="relative mb-24 md:mb-48 last:mb-0"
      initial={{ opacity: 0, x: isRight ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      {/* Mobile Layout */}
      <div className="md:hidden w-full px-4">
        <motion.div 
          className="relative mb-6 overflow-hidden rounded-lg"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <img 
            src={image} 
            alt={title}
            className="w-full h-64 object-cover transform transition-transform duration-700 hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </motion.div>
        
        <motion.h3 
          className="text-3xl font-serif text-[#c9ab81] mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {day}: {title}
        </motion.h3>
        <AnimatedText text={description} />
        
        {/* Mobile Timeline Dot */}
        <div className="absolute left-0 top-32 w-4 h-4 bg-[#c9ab81] rounded-full" />
        <div className="absolute left-[7px] top-36 bottom-0 w-px bg-gradient-to-b from-[#c9ab81] to-transparent" />
      </div>

      {/* Desktop Layout */}
      <div className={`hidden md:flex items-center ${isRight ? 'justify-end w-1/2 pr-16' : 'w-1/2 ml-auto pl-16'}`}>
        <div className="max-w-xl">
          <motion.div 
            className="relative mb-6 overflow-hidden rounded-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <img 
              src={image} 
              alt={title}
              className="w-full h-64 object-cover transform transition-transform duration-700 hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </motion.div>
          
          <motion.h3 
            className="text-4xl font-serif text-[#c9ab81] mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {day}: {title}
          </motion.h3>
          <AnimatedText text={description} />
        </div>
      </div>
      
      {/* Desktop Timeline Dot */}
      <div className="hidden md:block absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-[#c9ab81] rounded-full" />
    </motion.div>
  );
};

const Program = () => {
  const { t } = useTranslation();
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const days = [
    {
      day: t('program.days.0.day'),
      title: t('program.days.0.title'),
      description: t('program.days.0.description'),
      image: "https://images.unsplash.com/photo-1536623975707-c4b3b2af565d?q=80&w=2940&auto=format&fit=crop",
      align: "right"
    },
    {
      day: t('program.days.1-4.day'),
      title: t('program.days.1-4.title'),
      description: t('program.days.1-4.description'),
      image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=2102&auto=format&fit=crop",
      align: "left"
    },
    {
      day: t('program.days.5.day'),
      title: t('program.days.5.title'),
      description: t('program.days.5.description'),
      image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113194/1736490742353_2_mnicwt.jpg",
      align: "right"
    }
  ];

  return (
    <div className="pt-24">
      <ParallaxHero />

      {/* Journey Section */}
      <div className="relative min-h-screen py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,171,129,0.03)_0%,rgba(0,0,0,0)_70%)] opacity-60" />
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="relative">
            {/* Desktop Timeline Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#c9ab81] to-transparent" />

            {days.map((day, index) => (
              <DayCard key={index} {...day} />
            ))}
          </div>
        </div>
      </div>

      {/* Immersive Experience Section */}
      <div className="relative py-32 overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: "url('https://res.cloudinary.com/dansvy9a5/image/upload/v1739113190/1736183405038_2_rfj0d1.jpg')",
            scale
          }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-4">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl font-serif mb-6 text-white">
              {t('program.immersive.title')} <span className="text-[#c9ab81] italic">{t('program.immersive.subtitle')}</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              {t('program.immersive.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <motion.div
              className="relative"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#c9ab81] to-[#9AA581] opacity-20 blur rounded-lg" />
              <div className="relative bg-black/50 p-8 rounded-lg backdrop-blur-sm">
                <h3 className="text-2xl font-serif text-[#c9ab81] mb-4">{t('program.setting.title')}</h3>
                <p className="text-white/80 leading-relaxed">
                  {t('program.setting.description')}
                </p>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#c9ab81] to-[#9AA581] opacity-20 blur rounded-lg" />
              <div className="relative bg-black/50 p-8 rounded-lg backdrop-blur-sm">
                <h3 className="text-2xl font-serif text-[#c9ab81] mb-4">{t('program.guidance.title')}</h3>
                <p className="text-white/80 leading-relaxed">
                  {t('program.guidance.description')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,171,129,0.03)_0%,rgba(0,0,0,0)_70%)] opacity-60" />
        </div>

        <motion.div 
          className="relative max-w-4xl mx-auto px-4 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ y }}
        >
          <h2 className="text-5xl font-serif mb-8 text-white">
            {t('program.cta.title')}{' '}
            {t('program.cta.subtitle')}{' '}
            <span className="text-[#c9ab81] italic">{t('program.cta.highlight')}</span>
            {t('program.cta.question')}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {t('program.cta.description')}
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          >
            <a 
              href="https://docs.google.com/forms/d/e/1FAIpQLSc5mmPDqzUeexziQ_G5pmpiTNH2flL2p6DW5yWL2cu1lWC-6Q/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-block px-12 py-4 overflow-hidden"
            >
              <div className="absolute inset-0 w-0 bg-[#c9ab81] transition-all duration-[750ms] ease-out group-hover:w-full" />
              <span className="relative text-lg font-serif text-[#c9ab81] group-hover:text-black transition-colors duration-[750ms]">
                {t('program.cta.button')}
              </span>
              <div className="absolute inset-0 border border-[#c9ab81] pointer-events-none" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Program;